import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';
import { CommitmentCloud, CommitmentType } from '@cast/types-pricing';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { overrideOrganizationHeader } from '@cast/api-client';

const apiFn = apiClient.commitments.getCommitmentsUsageSummary;

type ResponseData = ApiResponse<typeof apiFn>;

const COMMITMENTS_USAGE_SUMMARY_QUERY_KEY = 'commitments-usage-summary';

const getCommitmentsUsageSummaryQueryKey = ({
  organizationId,
  commitmentId,
  cloud,
  commitmentTypes,
}: {
  organizationId: string | undefined;
  commitmentId?: string;
  cloud?: CommitmentCloud;
  commitmentTypes?: CommitmentType[];
}) =>
  [
    COMMITMENTS_USAGE_SUMMARY_QUERY_KEY,
    organizationId,
    commitmentId,
    cloud,
    commitmentTypes,
  ] as const;

const getCommitmentsUsageSummaryQueryOptions = ({
  organizationId,
  commitmentId,
  cloud,
  commitmentTypes,
  enabled,
}: {
  organizationId: string | undefined;
  commitmentId?: string;
  cloud?: CommitmentCloud;
  commitmentTypes?: CommitmentType[];
  enabled?: UseCommitmentsUsageSummaryQueryArgs<unknown>['enabled'];
}) =>
  queryOptions({
    queryKey: getCommitmentsUsageSummaryQueryKey({
      organizationId,
      commitmentId,
      cloud,
      commitmentTypes,
    }),
    queryFn: async () => {
      const { data } = await apiFn(
        {
          organizationId: organizationId!,
          ...(commitmentId
            ? { commitmentId }
            : {
                cloud,
                commitmentTypes,
                commitmentId: '-',
              }),
        },
        {
          // we need this override because sometimes we need to fetch data for enterprise organization (not child organization)
          headers: overrideOrganizationHeader(organizationId),
        }
      );
      return data;
    },
    enabled: makeIsEnabled(
      (!!cloud && !!commitmentTypes?.length) || !!commitmentId,
      enabled
    ),
  });

type UseCommitmentsUsageSummaryQueryArgs<SELECT> = QueryOptions<
  ResponseData,
  SELECT
> & {
  organizationId?: string;
  commitmentId?: string;
  cloud?: CommitmentCloud;
  commitmentTypes?: CommitmentType[];
};

export const useCommitmentsUsageSummaryQuery = <SELECT = ResponseData>({
  commitmentId,
  cloud,
  commitmentTypes,
  organizationId: organizationIdOverride,
  enabled,
  select,
  ...queryOpts
}: UseCommitmentsUsageSummaryQueryArgs<SELECT>) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = organizationIdOverride || currentOrganization?.id;

  return useQuery({
    ...getCommitmentsUsageSummaryQueryOptions({
      organizationId,
      commitmentId,
      cloud,
      commitmentTypes,
      enabled,
    }),
    ...(queryOpts as object),
    select,
  });
};
